import { Drawer, Loading } from '@/components';
import type { LocalTagsModel } from '@/features/tag/models';
import {
  useOrganizationRecursive,
  useRecursiveOrganizationFilteredByKey,
} from '@/features/tenant-manager/organization/_hooks/organization';
import dayjs from 'dayjs';
import type { ComponentProps, FC } from 'react';
import type { z } from 'zod';
import { LocalTagForm } from './LocalTagForm';

type LocalTag = z.infer<typeof LocalTagsModel>[number];

type Props = {
  open: boolean;
  onClose: () => void;
  selectedTag: LocalTag | undefined;
} & Pick<ComponentProps<typeof LocalTagForm>, 'onSubmit'>;

export const LocalTagFormDrawer: FC<Props> = ({
  selectedTag,
  open,
  onClose,
  onSubmit,
}) => {
  const { data: organizationIds } = useOrganizationRecursive({
    id: selectedTag?.organizationId ?? '',
    select: (v) => v.map((o) => o.id),
  });
  const nestedSharedOrganizationIds = useRecursiveOrganizationFilteredByKey({
    params: {
      ids: selectedTag?.sharedOrganizationIds ?? [],
    },
    options: {
      filterBy: 'id',
      separator: undefined,
      reverse: true,
    },
  });

  const isLoading =
    (selectedTag && !organizationIds) ||
    (nestedSharedOrganizationIds.isPending &&
      selectedTag?.sharedOrganizationIds.length);

  return (
    <Drawer size="large" open={open} onClose={onClose} destroyOnClose>
      {isLoading ? (
        <Loading />
      ) : (
        <LocalTagForm
          type={selectedTag ? 'edit' : 'new'}
          initialValues={{
            ...selectedTag,
            nestedSharedOrganizationIds: nestedSharedOrganizationIds.data ?? [],
            organizationId: organizationIds,
            period: [
              selectedTag?.startYear
                ? dayjs().year(selectedTag.startYear)
                : undefined,
              selectedTag?.endYear
                ? dayjs().year(selectedTag.endYear)
                : undefined,
            ],
          }}
          onSubmit={onSubmit}
        />
      )}
    </Drawer>
  );
};
